import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { ScanBarcode, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function BarcodeCameraScanner({ onScan, onClose }) {
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        let html5QrCode = null;

        const startScanner = async () => {
            try {
                html5QrCode = new Html5Qrcode("barcode-reader");
                scannerRef.current = html5QrCode;

                if (!mountedRef.current) return;

                await html5QrCode.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 280, height: 120 },
                        aspectRatio: 1.5,
                        formatsToSupport: [
                            0,  // QR_CODE
                            2,  // EAN_13
                            3,  // EAN_8
                            4,  // UPC_A
                            5,  // UPC_E
                            6,  // CODE_39
                            7,  // CODE_93
                            8,  // CODE_128
                            10, // ITF
                            12, // CODABAR
                        ],
                    },
                    (decodedText) => {
                        try {
                            const state = html5QrCode.getState();
                            if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                                html5QrCode.stop().catch(() => {});
                            }
                        } catch (e) { /* ignore */ }
                        onScan(decodedText);
                    },
                    () => {}
                );
            } catch (err) {
                if (!mountedRef.current) return;
                const msg = String(err);
                if (msg.includes('Permission')) {
                    setError("Accès caméra refusé. Autorisez l'accès dans les paramètres.");
                } else if (msg.includes('NotFound') || msg.includes('Requested device not found')) {
                    setError("Aucune caméra trouvée.");
                } else {
                    setError("Impossible de démarrer la caméra.");
                }
            }
        };

        startScanner();

        return () => {
            mountedRef.current = false;
            if (scannerRef.current) {
                try {
                    const state = scannerRef.current.getState();
                    if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                        scannerRef.current.stop().then(() => {
                            scannerRef.current.clear();
                        }).catch(() => {});
                    }
                } catch (e) { /* ignore */ }
            }
        };
    }, [onScan]);

    const handleClose = () => {
        if (scannerRef.current) {
            try {
                const state = scannerRef.current.getState();
                if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                    scannerRef.current.stop().catch(() => {});
                }
            } catch (e) { /* ignore */ }
        }
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[60] bg-black/90 flex flex-col items-center justify-center" data-testid="barcode-camera-overlay">
            <div className="relative w-full max-w-md mx-auto">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-2 text-white">
                        <ScanBarcode className="w-5 h-5" />
                        <span className="text-sm font-medium">Scanner le code-barres</span>
                    </div>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={handleClose}
                        className="text-white hover:bg-white/20 rounded-full"
                        data-testid="close-barcode-camera-btn"
                    >
                        <X className="w-5 h-5" />
                    </Button>
                </div>

                <div className="relative mx-4 rounded-2xl overflow-hidden bg-black">
                    <div id="barcode-reader" className="w-full" style={{ minHeight: 300 }} />
                </div>

                {error && (
                    <div className="mx-4 mt-4 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-center">
                        <p className="text-red-200 text-sm">{error}</p>
                    </div>
                )}

                <p className="text-white/50 text-xs text-center mt-4 px-4">
                    Positionnez le code-barres de votre carte dans le cadre
                </p>
            </div>
        </div>
    );
}
